import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  Tooltip,
  TextField,
} from "@mui/material";
import { useAiContext } from "../providers/AiProvider";
import { useCallback, useEffect, useState } from "react";
import { useSelectedProject } from "Hooks";
import { useUser } from "Hooks/useUser";
import { ApiACLRole } from "@incendium/api";
import { StopCircle } from "@mui/icons-material";

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "$compact",
})<{ $compact?: boolean }>(({ theme, $compact }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    alignItems: "flex-end",
    ...($compact
      ? {
          width: 400,
        }
      : {}),
    "& fieldset": {
      border: "none",
    },
  },
  ...($compact
    ? {
        "& .MuiInputBase-root": {
          padding: theme.spacing(1),
          height: 36,
        },
      }
    : {}),
}));

interface IAiTextFieldProps {
  compact?: boolean;
  onSubmit?: () => void;
}

function AiTextField({ compact = false, onSubmit }: IAiTextFieldProps) {
  const { selectedProject } = useSelectedProject();
  const { hasPermission } = useUser();
  const {
    currentQuestion,
    createSessionAndStream,
    isStreaming,
    isConnecting,
    stopStreaming,
    setCurrentQuestion,
    useMcpAgent,
    setUseMcpAgent,
    mcpModeLocked,
  } = useAiContext();
  const [value, setValue] = useState(currentQuestion);
  const isSuperAdmin = hasPermission(ApiACLRole.SUPER_ADMIN);

  useEffect(() => {
    setValue(currentQuestion);
  }, [currentQuestion]);

  const submit = useCallback(async () => {
    if (!value.trim() || isStreaming) {
      return;
    }
    // Create session and start streaming
    if (selectedProject?.id) {
      setCurrentQuestion(value);
      createSessionAndStream(value);
    }

    // Trigger the animation immediately
    onSubmit?.();
  }, [
    value,
    isStreaming,
    selectedProject?.id,
    createSessionAndStream,
    onSubmit,
    setCurrentQuestion,
  ]);

  return (
    <Stack spacing={1} sx={{ width: compact ? "auto" : "100%" }}>
      <StyledTextField
        $compact={compact}
        placeholder="Ask me anything"
        variant="outlined"
        multiline={!compact}
        rows={compact ? 1 : 4}
        fullWidth={!compact}
        value={value}
        InputProps={{
          readOnly: isStreaming || isConnecting,
          endAdornment:
            isStreaming || isConnecting ? (
              <InputAdornment position="end">
                <Tooltip title="Stop generating">
                  <IconButton
                    edge="end"
                    color="warning"
                    onClick={() => {
                      stopStreaming();
                    }}
                  >
                    <StopCircle />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : undefined,
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(e.target.value);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
      />
      {isSuperAdmin && (
        <FormControlLabel
          sx={{ alignSelf: "flex-start", ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={useMcpAgent}
              onChange={(_, checked) => setUseMcpAgent(checked)}
              disabled={isStreaming || isConnecting || mcpModeLocked}
            />
          }
          label="Use MCP agent"
        />
      )}
    </Stack>
  );
}

export default AiTextField;
